import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Receipt, Loader2, ExternalLink, Check, X, Inbox, ChevronLeft, ChevronRight, Trash2 } from "lucide-react";
import { toast } from "sonner";
import receiptService, { AdminReceipt, ReceiptStatus } from "../services/receipt.service";

interface AdminPaymentReceiptsProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const PAGE_SIZE = 10;

const STATUS_STYLES: Record<ReceiptStatus, React.CSSProperties> = {
  pending: { backgroundColor: "#fef3c7", color: "#92400e", border: "1px solid #fde68a" },
  approved: { backgroundColor: "#dcfce7", color: "#15803d", border: "1px solid #bbf7d0" },
  rejected: { backgroundColor: "#fee2e2", color: "#b91c1c", border: "1px solid #fecaca" },
};

const isReviewed = (status: ReceiptStatus) => status === "approved" || status === "rejected";

export function AdminPaymentReceipts({ open, onOpenChange }: AdminPaymentReceiptsProps) {
  const [receipts, setReceipts] = useState<AdminReceipt[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [clearingAll, setClearingAll] = useState(false);
  // Receipts whose document the admin has opened — approval is gated on this.
  const [viewedIds, setViewedIds] = useState<Set<string>>(new Set());
  // Pagination + the cross-page count of receipts the admin can clear.
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [clearableCount, setClearableCount] = useState(0);

  const fetchReceipts = useCallback(async (page: number): Promise<AdminReceipt[]> => {
    setLoading(true);
    try {
      const data = await receiptService.listReceipts(page, PAGE_SIZE);
      setReceipts(data.receipts);
      setTotal(data.pagination.total);
      setTotalPages(Math.max(1, data.pagination.pages));
      setClearableCount(data.clearableCount);
      setViewedIds(new Set()); // a fresh page must be re-opened before approving
      return data.receipts;
    } catch (error: any) {
      toast.error(error.message || "Failed to load receipts");
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch whenever the dialog is open or the page changes.
  useEffect(() => {
    if (open) fetchReceipts(currentPage);
  }, [open, currentPage, fetchReceipts]);

  // Reset to the first page when the dialog closes, so it reopens at the top.
  useEffect(() => {
    if (!open) setCurrentPage(1);
  }, [open]);

  const markViewed = (id: string) =>
    setViewedIds((prev) => new Set(prev).add(id));

  const review = async (id: string, status: "approved" | "rejected") => {
    setSavingId(id);
    try {
      const { receipt: updated, message } = await receiptService.reviewReceipt(id, status);
      setReceipts((prev) => prev.map((r) => (r._id === id ? { ...r, ...updated } : r)));
      // A pending receipt just became reviewed → it's now clearable.
      setClearableCount((c) => c + 1);
      toast.success(message);
    } catch (error: any) {
      toast.error(error.message || "Failed to update receipt");
    } finally {
      setSavingId(null);
    }
  };

  const clearOne = async (id: string) => {
    setSavingId(id);
    try {
      await receiptService.clearReceipt(id);
      toast.success("Receipt cleared from the list.");
      setClearableCount((c) => Math.max(0, c - 1));
      // If that emptied the current page, step back one (page 1 just refetches).
      const remaining = await fetchReceipts(currentPage);
      if (remaining.length === 0 && currentPage > 1) {
        setCurrentPage((p) => p - 1);
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to clear receipt");
    } finally {
      setSavingId(null);
    }
  };

  const clearAllReviewed = async () => {
    setClearingAll(true);
    try {
      const count = await receiptService.clearReviewed();
      toast.success(count === 1 ? "1 receipt cleared." : `${count} receipts cleared.`);
      if (currentPage === 1) {
        await fetchReceipts(1);
      } else {
        setCurrentPage(1); // triggers a refetch of page 1
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to clear receipts");
    } finally {
      setClearingAll(false);
    }
  };

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });

  const rangeEnd = Math.min(currentPage * PAGE_SIZE, total);
  const rangeStart = total === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl w-[92vw] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[#0f1f3d] flex items-center gap-2">
            <Receipt className="w-6 h-6 text-[#d4af37]" />
            Payment Receipts
          </DialogTitle>
          <DialogDescription>
            Review each uploaded receipt. Open the document, then approve — the student is
            emailed their access code automatically.
          </DialogDescription>
        </DialogHeader>

        {/* Toolbar row — kept out of the header so it never collides with the
            dialog's absolute top-right close (X) button. */}
        {clearableCount > 0 && (
          <div className="flex justify-end">
            <Button
              variant="outline"
              size="sm"
              className="flex-shrink-0"
              disabled={clearingAll || loading}
              onClick={clearAllReviewed}
              title="Hide all approved & rejected receipts from the list (they are kept, not deleted)"
            >
              {clearingAll ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Clear reviewed ({clearableCount})
            </Button>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-[#d4af37]" />
          </div>
        ) : receipts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="flex items-center justify-center rounded-full mb-4" style={{ width: 56, height: 56, backgroundColor: "rgba(15,31,61,0.06)" }}>
              <Inbox className="w-7 h-7 text-[#0f1f3d]" />
            </div>
            <p className="font-medium text-[#0f1f3d]">No receipts yet</p>
            <p className="text-sm text-gray-500 mt-1">Uploaded payment receipts will appear here for review.</p>
          </div>
        ) : (
          <>
            <div className="space-y-3" style={{ overflowY: "auto" }}>
              {receipts.map((r) => (
                <div
                  key={r._id}
                  className="flex flex-col sm:flex-row sm:items-center gap-4 rounded-xl p-4"
                  style={{ border: "1px solid rgba(15,31,61,0.1)" }}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-[#0f1f3d]" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {r.userId?.name || "Unknown student"}
                      </p>
                      <Badge style={STATUS_STYLES[r.status]}>{r.status}</Badge>
                    </div>
                    <p className="text-sm text-gray-500 mt-1" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {r.userId?.email || "—"} · {formatDate(r.createdAt)}
                      {r.amount != null ? ` · GH₵ ${r.amount.toLocaleString()}` : ""}
                    </p>
                  </div>

                  {r.driveWebViewLink && (
                    <a
                      href={r.driveWebViewLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => markViewed(r._id)}
                      className="flex items-center gap-1 text-sm font-medium flex-shrink-0"
                      style={{ color: "#0f1f3d" }}
                    >
                      <ExternalLink className="w-4 h-4" />
                      View
                    </a>
                  )}

                  {r.status === "pending" && (() => {
                    // Can't require viewing a document that doesn't exist, so a
                    // receipt without a link stays approvable.
                    const canApprove = !r.driveWebViewLink || viewedIds.has(r._id);
                    return (
                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={savingId === r._id}
                            onClick={() => review(r._id, "rejected")}
                          >
                            <X className="w-4 h-4 mr-2" />
                            Reject
                          </Button>
                          <Button
                            variant="gold"
                            size="sm"
                            disabled={savingId === r._id || !canApprove}
                            title={canApprove ? undefined : "Open the receipt before approving"}
                            onClick={() => review(r._id, "approved")}
                          >
                            {savingId === r._id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Check className="w-4 h-4 mr-2" />
                                Approve
                              </>
                            )}
                          </Button>
                        </div>
                        {!canApprove && (
                          <p className="text-xs text-gray-400">Open the receipt to enable approval</p>
                        )}
                      </div>
                    );
                  })()}

                  {isReviewed(r.status) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="flex-shrink-0"
                      style={{ color: "#b91c1c" }}
                      disabled={savingId === r._id}
                      title="Hide this receipt from the list (it is kept, not deleted)"
                      onClick={() => clearOne(r._id)}
                    >
                      {savingId === r._id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <X className="w-4 h-4 mr-2" />
                          Clear
                        </>
                      )}
                    </Button>
                  )}
                </div>
              ))}
            </div>

            {total > 0 && (
              <div className="flex items-center justify-between pt-4 mt-1 border-t" style={{ borderColor: "rgba(15,31,61,0.1)" }}>
                <div className="text-sm text-gray-500">
                  Showing <span className="font-medium">{rangeStart}–{rangeEnd}</span> of{" "}
                  <span className="font-medium">{total}</span>
                </div>
                {totalPages > 1 && (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1 || loading}
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Previous
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages || loading}
                    >
                      Next
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
